import { createAsyncThunk } from '@reduxjs/toolkit';

import { Interactions } from '../../../constants';
import type {
  AccountDataProps,
  MediaPlatformImage,
  PublicInfo,
  ThunkApiConfig,
} from '../../../types';
import type { AccountData } from '../../../types/server';
import { ServerErrorCode } from '../../../types/server';
import { maybeUpdateAvatar } from '../../slices/member/thunk';
import {
  getPublicInfo,
  getPublicInfoWithMediaPicture,
  hasMemberAvatarURLChanged,
} from './utils';

const hasProfileInfoChanged = (
  initialPublicInfo: PublicInfo,
  updatedPublicInfo: PublicInfo,
) => {
  return (
    updatedPublicInfo.name !== initialPublicInfo.name ||
    updatedPublicInfo.title !== initialPublicInfo.title ||
    hasMemberAvatarURLChanged(initialPublicInfo, updatedPublicInfo)
  );
};

const updateAccountData = createAsyncThunk<
  AccountData,
  AccountDataProps,
  ThunkApiConfig
>(
  'accountData/update',
  async (
    { currentFields, changedPrivacyFields, currentMember },
    {
      rejectWithValue,
      dispatch,
      getState,
      extra: { flowAPI, profileInfoChangeSubject, myAccountMiddlewareService },
    },
  ) => {
    try {
      flowAPI.panoramaClient
        .transaction(Interactions.AccountDataUpdate)
        .start();
      const state = getState();
      const initialMember = state.member;
      const initialFields = state.fields;
      const changedPrivacyFieldsIds = changedPrivacyFields.map(
        (field) => field.id,
      );

      let memberWithMaybeUpdatedAvatar = initialMember;
      let profileCardPicture: MediaPlatformImage | undefined;

      const maybeUpdateAvatarResult = await dispatch(
        maybeUpdateAvatar({ currentMember }),
      );

      if (maybeUpdateAvatar.fulfilled.match(maybeUpdateAvatarResult)) {
        const updateAvatarPayload = maybeUpdateAvatarResult.payload;
        const { avatarPhoto, mediaPlatformPicture } = updateAvatarPayload;
        memberWithMaybeUpdatedAvatar = {
          ...initialMember,
          profile: { ...initialMember.profile, photo: avatarPhoto },
        };
        profileCardPicture = mediaPlatformPicture;
      }

      const payload = {
        member: memberWithMaybeUpdatedAvatar,
        fields: currentFields,
        changedPrivacyFieldsIds,
      };

      const data = await myAccountMiddlewareService.updateAccountData(payload);

      if ('error' in data) {
        return rejectWithValue(data.error);
      }

      const initialProfileInfo = getPublicInfo(initialFields, initialMember);
      const currentProfileInfo = getPublicInfo(currentFields, currentMember);

      if (hasProfileInfoChanged(initialProfileInfo, currentProfileInfo)) {
        const publicInfoWithMediaPicture = getPublicInfoWithMediaPicture(
          initialProfileInfo,
          currentProfileInfo,
          profileCardPicture,
        );

        profileInfoChangeSubject?.notifyObservers(publicInfoWithMediaPicture);
      }

      flowAPI.panoramaClient
        .transaction(Interactions.AccountDataUpdate)
        .finish();

      return data;
    } catch (error) {
      return rejectWithValue(ServerErrorCode.INTERNAL);
    }
  },
);

export const accountDataThunk = { updateAccountData };
